import { EmptyState, Icon } from '@/components';
import { cn } from '@/lib/cn';
import { formatCalendarDate } from '@/lib/datetime';
import { For, type JSX, Show, createSignal } from 'solid-js';
import { WHATS_NEW, type WhatsNewEntry } from './whats-new';

const PREVIEW_COUNT = 5;

export const WhatsNewWidget = (props: { entries?: WhatsNewEntry[] }): JSX.Element => {
  const entries = () => props.entries ?? WHATS_NEW;
  const [showAll, setShowAll] = createSignal(false);
  // Index-keyed: "show more" only appends, so indices stay stable.
  const [open, setOpen] = createSignal<Record<number, boolean>>({});
  const isOpen = (i: number) => open()[i] ?? i === 0;
  const visible = () => (showAll() ? entries() : entries().slice(0, PREVIEW_COUNT));

  return (
    <section class="rounded-xl border border-neutral-200 bg-surface p-5 shadow-sm dark:border-neutral-800 dark:bg-surface-dark">
      <h2 class="mb-3 text-base font-semibold">What's new</h2>
      <Show
        when={entries().length > 0}
        fallback={<EmptyState title="ยังไม่มีอัปเดต" body="การเปลี่ยนแปลงใหม่ๆ จะแสดงที่นี่หลัง deploy" />}
      >
        <ul class="divide-y divide-neutral-200 dark:divide-neutral-800">
          <For each={visible()}>
            {(entry, i) => (
              <li>
                <button
                  type="button"
                  aria-expanded={isOpen(i())}
                  onClick={() => setOpen((prev) => ({ ...prev, [i()]: !isOpen(i()) }))}
                  class="flex w-full items-center gap-3 rounded-lg py-3 text-left transition-colors duration-200 ease-out hover:text-brand-600 motion-reduce:transition-none dark:hover:text-brand-400"
                >
                  <span class="w-24 shrink-0 text-xs text-neutral-500">
                    {formatCalendarDate(entry.date) || entry.date}
                  </span>
                  <span class="min-w-0 flex-1 text-sm font-medium">{entry.title}</span>
                  <Icon
                    name="angle-small-down"
                    class={cn(
                      'text-neutral-400 transition-transform duration-200 ease-out motion-reduce:transition-none',
                      isOpen(i()) && 'rotate-180',
                    )}
                  />
                </button>
                <div
                  class={cn(
                    'grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none',
                    isOpen(i()) ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                  )}
                >
                  <div class="overflow-hidden" inert={!isOpen(i())}>
                    <ul class="list-disc space-y-1 pb-3 pl-[7.75rem] pr-2 text-sm text-neutral-700 dark:text-neutral-300">
                      <For each={entry.items}>{(item) => <li>{item}</li>}</For>
                    </ul>
                  </div>
                </div>
              </li>
            )}
          </For>
        </ul>
        <Show when={!showAll() && entries().length > PREVIEW_COUNT}>
          <button
            type="button"
            onClick={() => setShowAll(true)}
            class="mt-2 text-sm font-medium text-brand-600 hover:underline dark:text-brand-400"
          >
            ดูเพิ่มเติม
          </button>
        </Show>
      </Show>
    </section>
  );
};
